import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { FC, ReactNode, useEffect, useState } from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useSignOut } from "react-auth-kit";
import { useDispatch } from "react-redux";
import styles from "@/components/forms/errorHandling/genericErrorMessageModal.module.css";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { NavLink } from "react-router-dom";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  ErrorLine,
  HelpLinksByField,
  normalizeApiErrorToLines,
} from "@/components/forms/errorHandling/errorMessageLines";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";

interface Props {
  title: string;
  confirmButtonText?: string;
  error: any;
  onClosehandler?: () => void;
  onSubmitHandler?: () => void;
  status?: any;
  errorEntity?: string;
  helpLinksByField?: HelpLinksByField;
}

const GenericErrorMessageModal: FC<Props> = ({
  title,
  confirmButtonText,
  error,
  onClosehandler,
  onSubmitHandler,
  status,
  errorEntity,
  helpLinksByField,
}) => {
  const [modalOpen, setModalOpen] = useState<boolean>(true);
  const signOut = useSignOut();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      setModalOpen(true);
    }
  }, [error]);

  // side-effect error values, kept out of render on purpose
  useEffect(() => {
    if (error === "logout") {
      dispatch({ type: "clear" });
      signOut();
    }
    if (typeof error === "function") {
      error();
    }
  }, [error]);

  let buttonText = t("ok");
  if (confirmButtonText) {
    buttonText = confirmButtonText;
  }

  let lines: ErrorLine[];
  try {
    lines = normalizeApiErrorToLines(error, t, status, helpLinksByField);
  } catch {
    lines = [{ primary: String(error) }];
  }

  const renderSecondary = (line: ErrorLine): ReactNode => {
    if (line.variant === "conflict" || line.links?.length) {
      return (
        <>
          {line.secondary && (
            <span style={{ display: "block", marginBottom: "0.5em" }}>
              {line.secondary}
            </span>
          )}
          {line.links?.map((link, index) => (
            <div key={index} style={{ padding: "0.1rem" }}>
              <NavLink
                to={link.to}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.navLink}
              >
                {link.label}
              </NavLink>
            </div>
          ))}
        </>
      );
    }
    if (line.details && line.details.length > 0) {
      return (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}
        >
          {line.details.map((detail, index) => (
            <span key={index}>
              <strong>{detail.label}</strong> {detail.value}
            </span>
          ))}
        </div>
      );
    }
    return line.secondary;
  };

  const renderLine = (line: ErrorLine, index: number) => (
    <ListItem key={index} alignItems="flex-start">
      <ListItemAvatar>
        {line.variant === "conflict" ? (
          <FiberManualRecordIcon className={styles.conflictIcon} />
        ) : (
          <ErrorOutlineIcon className={styles.errorIcon} />
        )}
      </ListItemAvatar>
      <ListItemText
        primaryTypographyProps={{
          className:
            line.variant === "conflict"
              ? styles.listItemConflictTextPrimary
              : styles.listItemTextPrimary,
          style: { whiteSpace: "pre-line" },
        }}
        secondaryTypographyProps={{ component: "div" }}
        primary={line.primary}
        secondary={renderSecondary(line)}
      />
    </ListItem>
  );

  const message = lines.length > 0 ? lines.map(renderLine) : <List />;

  const handleDismiss = () => {
    setModalOpen(false);
    onClosehandler?.();
  };

  const cancelClose = useModalDismiss(modalOpen, handleDismiss);

  const renderButtons = () => {
    if (status === 409 && errorEntity === "entityTranslation") {
      return (
        <div className={styles.dialogActionsWrapper}>
          <GenericButton onClick={handleDismiss}>
            {t("cancel")}
          </GenericButton>
          <GenericButton
            onClick={() => {
              setModalOpen(false);
              onSubmitHandler?.();
            }}
          >
            {buttonText}
          </GenericButton>
        </div>
      );
    }
    return (
      <GenericButton onClick={handleDismiss}>
        {buttonText}
      </GenericButton>
    );
  };

  return (
    <Dialog
      open={modalOpen}
      scroll="paper"
      {...cancelClose}
    >
      <DialogTitle className={styles.dialogTitle}>{title}</DialogTitle>
      <DialogContent dividers>{message}</DialogContent>
      <DialogActions>{renderButtons()}</DialogActions>
    </Dialog>
  );
};

export default GenericErrorMessageModal;
